import type { Filter, Node } from 'turndown';

import TurndownService from 'turndown';

import { htmlTags } from './html-tags';

// ----------------------------------------------------------------------

type INode = HTMLElement & {
  isBlock: boolean;
};

const excludeTags = ['pre', 'code'];

const turndownService = new TurndownService({ codeBlockStyle: 'fenced', fence: '```' });

const filterTags = htmlTags.filter((item) => !excludeTags.includes(item)) as Filter;

/**
 * Custom rule
 * https://github.com/mixmark-io/turndown/issues/241#issuecomment-400591362
 */
turndownService.addRule('keep', {
  filter: filterTags,
  replacement(content: string, node: Node) {
    const { isBlock, outerHTML } = node as INode;

    return node && isBlock ? `\n\n${outerHTML}\n\n` : outerHTML;
  },
});

// ----------------------------------------------------------------------

export function htmlToMarkdown(html: string) {
  return turndownService.turndown(html);
}
// ----------------------------------------------------------------------

export function isMarkdownContent(content: string) {
  const markdownPatterns = [
    /* Headings at any level */
    /^(#{1,6})\s/m,
    /* Bulleted list, ordered list */
    /^(\*|-|\+|\d+\.)\s/m,
    /* Blockquotes */
    /^>\s/m,
    /* Horizontal Rule */
    /^-{3,}$|^\*{3,}$|^_{3,}$/m,
    /* Code (inline and block) */
    /`[^`]+`|^```[\s\S]*?```/m,
    /* Bold, italic */
    /\*{1,2}[^*]+\*{1,2}|_{1,2}[^_]+_{1,2}/,
    /* Links and Images */
    /!\[[^\]]*\]\([^)]+\)|\[[^\]]+\]\([^)]+\)/,
    /* Tables (simplified, assuming at least one row and one header) */
    /^\|.*\|$|\|.*\|\s*$/m,
    /* Fenced code blocks */
    /^```[\s\S]*?```/m,
    /* LaTeX (inline and block) */
    /\$[^$]+\$|^\$\$[\s\S]*?\$\$$/m,
    /* LaTeX commands */
    /\text\{[^}]+\}|\frac\{[^}]+\}\{[^}]+\}/m,
    // /* More inclusive MermaidJS diagrams detection */
    // /^```.*[\s\S]*?(title\s+\S+|["\w]+\s*:\s*\d+|["\w]+\s*:\s*["\w]+)[\s\S]*?```/m,
  ];

  const htmlPatterns = [
    /<!DOCTYPE html>/i, // Detects the DOCTYPE declaration
  ];

  // Check if the content is HTML by searching for any HTML-specific patterns
  for (const pattern of htmlPatterns) {
    if (pattern.test(content)) {
      return false; // It's HTML, not markdown
    }
  }

  // Check if any of the patterns match
  return markdownPatterns.some((pattern) => pattern.test(content));
}
